import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { CLASSIFICATION_LABEL } from '../../components/audit/AuditVocabulary.js';
import { fetchAuditDashboard, type FindingClassification } from '../../lib/audit-api.js';
import { t } from '../../lib/i18n.js';

/**
 * The Audit Dashboard (spec §11 key screen 10).
 *
 * It computes nothing. Every figure comes from the server, which gets them from
 * the same tested helper the rest of App 3 uses — the dashboard is the last
 * place a second implementation of a count should appear, because it is the
 * place people quote from.
 *
 * The one thing it insists on is **labelling open findings and open corrective
 * actions as different numbers**. One finding can spawn several CARs
 * (ADR-0012), so "12 open" under a single heading would be true of neither.
 */
export function AuditDashboard() {
  const dashboard = useQuery({ queryKey: ['audit-dashboard'], queryFn: fetchAuditDashboard });
  const summary = dashboard.data?.summary;

  return (
    <div className="min-h-full">
      <AppTopBar app="audit" />
      <Breadcrumbs app="audit" trail={['Dashboard']} />

      <main className="mx-auto max-w-5xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Audit dashboard")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Only the audits your scope reaches are counted.")}
        </p>

        {dashboard.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}
        {dashboard.error && (
          <p className="mt-6 text-tone-critical">{t("The dashboard could not be loaded.")}</p>
        )}

        {summary && (
          <>
            <section className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Tile label={t("Audits")} value={summary.audits.total} note={t("{0} open", { 0: summary.audits.open })} />
              <Tile
                label={t("Findings")}
                value={summary.findings}
                note={t("{0} open", { 0: summary.findingsOpen })}
              />
              <Tile
                label={t("Corrective actions")}
                value={summary.cars}
                note={t("{0} open", { 0: summary.carsOpen })}
              />
              <Tile
                label={t("Audits closed")}
                value={summary.audits.closed}
                note={t("every finding resolved")}
              />
            </section>

            <p className="mt-3 text-sm text-content-secondary">
              {t("Findings and corrective actions are counted separately on purpose: one finding can spawn one corrective action per responsible department, so the two numbers answer different questions.")}
            </p>

            <section className="mt-8">
              <h2 className="m-0 text-lg font-semibold">{t("Findings by classification")}</h2>
              {summary.findings === 0 ? (
                <p className="mt-2 text-sm text-content-secondary">
                  {t("No findings have been raised in the audits your scope reaches.")}
                </p>
              ) : (
                <ul className="mt-3 list-none space-y-2 p-0">
                  {(Object.keys(CLASSIFICATION_LABEL) as FindingClassification[]).map((key) => {
                    const count = summary.byClassification[key] ?? 0;
                    const share = summary.findings > 0 ? (count / summary.findings) * 100 : 0;
                    return (
                      <li key={key} className="text-sm">
                        <div className="flex items-baseline justify-between">
                          <span>{t(CLASSIFICATION_LABEL[key])}</span>
                          <span className="text-content-secondary">{count}</span>
                        </div>
                        <div
                          className="mt-1 h-2 rounded bg-surface-sunken"
                          role="img"
                          aria-label={t("{0}: {1} of {2}", { 0: CLASSIFICATION_LABEL[key], 1: count, 2: summary.findings })}
                        >
                          <div
                            className="h-2 rounded bg-ag-purple"
                            style={{ width: `${share}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <p className="mt-8 text-sm">
              <Link to="/audit/findings">{t("Open the findings log")}</Link>{' '}{t("to see what is outstanding and act on it.")}
            </p>
          </>
        )}
      </main>
    </div>
  );
}

function Tile({ label, value, note }: { label: string; value: number; note: string }) {
  return (
    <div className="rounded-md border border-border bg-surface p-5 shadow-card">
      <p className="m-0 text-sm text-content-secondary">{label}</p>
      <p className="m-0 mt-1 text-3xl font-semibold">{value}</p>
      <p className="m-0 mt-1 text-xs text-content-secondary">{note}</p>
    </div>
  );
}
